/* Controlled Chaos — Add to Cart upsell toast

   Fires on a SUCCESSFUL add to cart. Hooks the theme's own 'cart:update' event
   (dispatched by product-form.js as CartAddEvent with source 'product-form-component'),
   so it works from the product page AND the quick-add modal, and never fires when the
   add failed (didError) or when a cart line is edited in the drawer.

   The message and timing live in CC_TOAST_CONFIG below — edit there, not in the logic.

   IMPORTANT (discount honesty): the offer text must match a real discount.
   As of setup, the store's matching discount is the CODE "FF15" (10% off, minimum
   quantity 2) — a code, not an automatic discount, so customers must enter it.
   Set `code` below to '' ONLY if you convert it to an automatic discount, otherwise
   the toast promises something the customer can't actually claim at checkout.
*/
(function () {
  'use strict';

  var CC_TOAST_CONFIG = {
    eyebrow: 'Added to bag',
    // Shown when the cart does NOT yet qualify for the offer
    message: 'Unlock <strong>10% off your entire order</strong> when you buy 2 or more items!',
    // Shown once the cart already has 2+ items — the offer is earned, don't keep upselling
    messageUnlocked: '<strong>10% off unlocked.</strong> You’ve got 2+ items in your bag.',
    // Discount code customers must enter. Set to '' if the discount is automatic.
    code: 'FF15',
    codeHint: 'Tap to copy · apply at checkout',
    minQuantity: 2,
    duration: 6500,          // visible time in ms before auto fade-out
    fadeDuration: 450        // must match .cc-toast.is-fading transition in the CSS
  };

  var toast, msgEl, eyebrowEl, codeBtn, timerEl;
  var hideTimer = null, removeTimer = null, startedAt = 0, remaining = 0, paused = false;

  function build() {
    toast = document.querySelector('[data-cc-toast]');
    if (!toast) return false;
    eyebrowEl = toast.querySelector('[data-cc-toast-eyebrow]');
    msgEl = toast.querySelector('[data-cc-toast-msg]');
    codeBtn = toast.querySelector('[data-cc-toast-code]');
    timerEl = toast.querySelector('[data-cc-toast-timer]');

    toast.style.setProperty('--cc-toast-duration', CC_TOAST_CONFIG.duration + 'ms');

    var closeBtn = toast.querySelector('[data-cc-toast-close]');
    if (closeBtn) closeBtn.addEventListener('click', hide);

    // Pause the countdown while the customer is reading / hovering
    toast.addEventListener('mouseenter', pause);
    toast.addEventListener('mouseleave', resume);
    toast.addEventListener('focusin', pause);
    toast.addEventListener('focusout', resume);

    if (codeBtn) {
      codeBtn.addEventListener('click', function () {
        var code = codeBtn.getAttribute('data-cc-toast-code') || '';
        if (!code) return;
        var done = function () {
          codeBtn.setAttribute('data-copied', 'true');
          var label = codeBtn.querySelector('[data-cc-toast-code-label]');
          if (label) label.textContent = 'Copied ✓';
        };
        if (navigator.clipboard && navigator.clipboard.writeText) {
          navigator.clipboard.writeText(code).then(done).catch(function () {});
        } else {
          var ta = document.createElement('textarea');
          ta.value = code;
          ta.style.position = 'fixed';
          ta.style.opacity = '0';
          document.body.appendChild(ta);
          ta.select();
          try { document.execCommand('copy'); done(); } catch (e) {}
          document.body.removeChild(ta);
        }
      });
    }
    return true;
  }

  function clearTimers() {
    if (hideTimer) { clearTimeout(hideTimer); hideTimer = null; }
    if (removeTimer) { clearTimeout(removeTimer); removeTimer = null; }
  }

  function startCountdown(ms) {
    startedAt = Date.now();
    remaining = ms;
    paused = false;
    hideTimer = setTimeout(hide, ms);
  }

  function pause() {
    if (!toast || !toast.classList.contains('is-visible') || paused || !hideTimer) return;
    paused = true;
    clearTimeout(hideTimer);
    hideTimer = null;
    remaining = Math.max(0, remaining - (Date.now() - startedAt));
    if (timerEl) {
      var elapsed = 1 - (remaining / CC_TOAST_CONFIG.duration);
      toast.style.setProperty('--cc-toast-progress', String(Math.min(1, Math.max(0, elapsed))));
    }
    toast.classList.add('is-paused');
  }

  function resume() {
    if (!toast || !paused) return;
    paused = false;
    toast.classList.remove('is-paused');
    // Restart the bar transition from where it stopped
    toast.style.setProperty('--cc-toast-duration', remaining + 'ms');
    void toast.offsetWidth;
    startCountdown(remaining);
  }

  function hide() {
    if (!toast) return;
    clearTimers();
    paused = false;
    toast.classList.remove('is-counting', 'is-paused');
    toast.classList.add('is-fading');
    removeTimer = setTimeout(function () {
      toast.classList.remove('is-visible', 'is-fading');
      toast.setAttribute('aria-hidden', 'true');
    }, CC_TOAST_CONFIG.fadeDuration);
  }

  function show(qualified) {
    if (!toast) return;
    clearTimers();

    if (eyebrowEl) eyebrowEl.textContent = CC_TOAST_CONFIG.eyebrow;
    if (msgEl) msgEl.innerHTML = qualified ? CC_TOAST_CONFIG.messageUnlocked : CC_TOAST_CONFIG.message;

    if (codeBtn) {
      if (CC_TOAST_CONFIG.code) {
        codeBtn.hidden = false;
        codeBtn.setAttribute('data-cc-toast-code', CC_TOAST_CONFIG.code);
        codeBtn.removeAttribute('data-copied');
        codeBtn.title = CC_TOAST_CONFIG.codeHint;
        var label = codeBtn.querySelector('[data-cc-toast-code-label]');
        if (label) label.textContent = 'Code ' + CC_TOAST_CONFIG.code;
      } else {
        codeBtn.hidden = true;
      }
    }

    // Reset animation state, then show
    toast.classList.remove('is-fading', 'is-paused', 'is-counting');
    toast.style.setProperty('--cc-toast-duration', '0ms');
    void toast.offsetWidth;
    toast.setAttribute('aria-hidden', 'false');
    toast.classList.add('is-visible');
    toast.style.setProperty('--cc-toast-duration', CC_TOAST_CONFIG.duration + 'ms');
    void toast.offsetWidth;
    toast.classList.add('is-counting');

    startCountdown(CC_TOAST_CONFIG.duration);
  }

  // Ask the cart how many items are in it, so we don't upsell an already-qualifying cart
  function showForCart() {
    fetch(window.Theme && Theme.routes && Theme.routes.cart_url ? Theme.routes.cart_url + '.js' : '/cart.js', {
      headers: { Accept: 'application/json' }
    })
      .then(function (r) { return r.json(); })
      .then(function (cart) {
        var count = cart && typeof cart.item_count === 'number' ? cart.item_count : 0;
        show(count >= CC_TOAST_CONFIG.minQuantity);
      })
      .catch(function () {
        show(false); // network hiccup: fall back to the upsell message
      });
  }

  function onCartUpdate(event) {
    var detail = event && event.detail ? event.detail : {};
    var data = detail.data || {};
    // Only genuine, successful adds from a product form / quick add
    if (data.didError) return;
    if (data.source !== 'product-form-component') return;
    showForCart();
  }

  function init() {
    if (!build()) return;
    document.addEventListener('cart:update', onCartUpdate);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
